import { type AgentsSurface } from '@lib/analytics/agentsExit';
import { askAgent } from '@lib/starzero-agents';
import { debugStore } from '@stores/Debug';
import { SegmentType } from '@stores/Library/LibrarySearch';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Where a verb was invoked from. `surface` is the page, `control` the specific
 * control — the same pair `AgentsExitContext` uses, so an agent click reports
 * one consistent origin to both `Library Verb Clicked` and `Agents App Opened`.
 */
export interface LibraryVerbOrigin {
  surface: AgentsSurface;
  control: string;
}

/**
 * What the two library-scoped verbs DO, in one place. What they're called and which glyph
 * they carry lives in `@lib/verbs`; this file owns only the behaviour and the tracking.
 *
 * Every surface that offers them goes through here, so none of them repeats the search-mode
 * rule or the tracking call:
 *
 * - the labelled action row inside a library (`LibraryActionRow`)
 * - the library card's hover rail and the desktop table's Name cell (`buildLibraryRailItems`)
 * - the Libraries page header pills, labelled and collapsed (`LibrariesVerbPills` — two
 *   `control` values, so the two forms stay comparable)
 * - the mobile context drawer (`MobileLibraryContextDrawer`)
 * - three Workspace signpost CTAs: both search CTAs via `openLibrarySearch`, and the
 *   agent CTA on the work-with-a-library card
 *
 * NOT the desktop kebabs — they no longer carry these verbs at all, since every surface that
 * mounts one shows a rail beside it. `LibraryMenuButton` doesn't import this hook.
 *
 * If you add a surface, route it through here rather than calling `askAgent` / `navigate`
 * yourself. Both bypasses have happened, and both were invisible: a hand-rolled `navigate`
 * sent transcript-only libraries into an empty visual search, and a direct `askAgent` left the
 * Workspace CTA absent from `Library Verb Clicked` while looking identical to every other
 * agent button.
 *
 * Note the verbs are deliberately NOT symmetrical in where they land: the agent
 * is a cross-origin handoff (`askAgent`), moments is in-app navigation.
 */
export function useLibraryVerbs() {
  const navigate = useNavigate();
  const libraryManager = useLibraryManager();

  return useMemo(
    () => ({
      workWithAgent(libraryId: string, origin: LibraryVerbOrigin) {
        trackingStore.libraryVerbClicked({ verb: 'work-with-agent', libraryId, ...origin });
        // `askAgent` fires `Agents App Opened` itself and derives `libraryId`
        // from the URL it builds, so nothing is threaded twice.
        askAgent(libraryId, '_self', origin);
      },

      /**
       * `libraryIds` takes one id or several. Search genuinely spans libraries — the
       * `libs=` param is a comma list and `LibraryManagerMultiLibrarySearch` fans the
       * query out per library and merges by score — so a caller that lets the user pick a
       * set can pass the set. The agent verb has no equivalent: Agents is one library only
       * (see `starzero-agents.ts`).
       *
       * Scope is committed through the URL rather than by writing
       * `selectedLibraryIdsToSearch` first: `SearchParamsInURL` applies `libs=` on arrival,
       * so a caller never has to mutate the persisted search scope to navigate into it.
       *
       * Returns the mode it navigated to, for callers that also report the search in their own
       * event — the mode is decided here, so a caller can't state it correctly by guessing.
       */
      findMoments(libraryIds: string | string[], origin: LibraryVerbOrigin): SegmentType {
        const ids = Array.isArray(libraryIds) ? libraryIds : [libraryIds];
        trackingStore.libraryVerbClicked({
          verb: 'find-moments',
          // A single id is the id; a set is reported by size. Sending the first of many
          // would read in the dashboard as "this library was searched", which isn't what
          // happened.
          libraryId: ids.length === 1 ? ids[0] : undefined,
          libraryCount: ids.length,
          ...origin,
        });
        // Unified search is disabled product-wide (the debug flag "Enable unified
        // search" revives it), so the default is visual — matching
        // `WorkspacePage`'s `defaultSearchMode`. Read at click time, not render
        // time, so toggling the flag doesn't need a remount.
        //
        // Transcript-only libraries have to land in transcript mode. Visual is disabled for
        // them and returns nothing, and because `'visual'` is a *valid* segment type
        // `SearchParamsInURL` accepts it verbatim and never reaches the scope-aware
        // `fallbackResultType` — so sending them to visual produced an empty result set that
        // read as a broken search rather than a wrong mode.
        //
        // The store owns the rule (`allScopeIsTranscriptOnly` delegates to the same method):
        // transcript only when EVERY target is transcript-only. A mixed set keeps visual,
        // because the libraries that do have visual indexing would otherwise silently lose it.
        const mode: SegmentType = libraryManager.multiLibSearch.allTranscriptOnly(ids)
          ? 'transcript'
          : debugStore.showUnifiedSearch
            ? 'unified'
            : 'visual';
        navigate(`/search?libs=${ids.join(',')}&search=${mode}`);
        return mode;
      },
    }),
    // Both deps are stable for the provider's lifetime — `libraryManager` is held in the
    // context provider's `useState`, `navigate` is stable in react-router v6 — so the returned
    // object keeps its identity. Worth having because consumers memoise handlers built from
    // these (e.g. each `NameCell` in `LibrariesTable`), and an unstable verb object would
    // invalidate every one of them on each render.
    [navigate, libraryManager],
  );
}
